/* assets/littleg-kid-opening.js — LITTLEG-KID-OPENING-RUNTIME-01
 *
 * Canonical lifecycle owner for the LittleG Kid Map Opening.
 *
 * State:
 *   waiting -> entering -> ready
 *
 * Responsibility boundary:
 * - Owns Opening state transition only.
 * - Consumes onboarding/invite projection.
 * - Does not own Mapbox.
 * - Does not own social data.
 * - Does not persist opening state.
 * - Does not mutate URL.
 * - Does not open/close Passport, Search or Memory View.
 */

(function (window, document) {
  'use strict';

  var VERSION = '1.0.1';
  var ROOT_SELECTOR = '[data-littleg-kid-map]';
  var OPENING_SELECTOR = '[data-kid-opening]';
  var ENTER_SELECTOR = '[data-kid-opening-enter]';
  var EXIT_DURATION_MS = 1050;
  var LOCK_CLASS = 'littleg-kid-opening-lock';

  if (window.__LITTLEG_KID_OPENING__ === VERSION) return;
  window.__LITTLEG_KID_OPENING__ = VERSION;

  function q(root, selector) {
    return root ? root.querySelector(selector) : null;
  }

  function qa(root, selector) {
    return root ? Array.prototype.slice.call(root.querySelectorAll(selector)) : [];
  }

  function reducedMotion() {
    try {
      return (
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
    } catch (_) {
      return false;
    }
  }

  function isDashboardMapEntry() {
    try {
      var path = window.location.pathname || '';
      if (!/\/pages\/littleg-map\/?$/.test(path)) return false;
      // The head marker also covers one-shot Dashboard navigation when a
      // locale redirect loses the source query parameter.
      if (document.documentElement.getAttribute('data-littleg-map-entry') === 'dashboard')
        return true;
      return new URLSearchParams(window.location.search).get('source') === 'dashboard';
    } catch (_) {
      return false;
    }
  }

  function friendShareEntry() {
    try {
      var params = new URLSearchParams(window.location.search);
      return String(params.get('source') || '').trim() === 'friend_share';
    } catch (_) {
      return false;
    }
  }

  function socialRows() {
    var family = window.LittleGFamilyData;
    var graph = family && family.socialGraph;

    if (!graph || typeof graph.listPlaces !== 'function') return [];

    try {
      var rows = graph.listPlaces();
      return Array.isArray(rows) ? rows : [];
    } catch (_) {
      return [];
    }
  }

  function confirmedRows() {
    return socialRows().filter(function (row) {
      return (
        row &&
        row.visibility === 'family_private' &&
        row.privacyConfirmed === true &&
        row.parentConfirmed === true &&
        row.approvalStatus === 'approved'
      );
    });
  }

  function resolveVariant(root) {
    if (root.getAttribute('data-kid-invite-active') === 'true' || friendShareEntry()) {
      return 'invite';
    }

    var onboarding = root.getAttribute('data-kid-onboarding-state');
    if (onboarding === 'returning') return 'returning';
    if (onboarding === 'first-use') return 'first-use';

    return confirmedRows().length ? 'returning' : 'first-use';
  }

  function projectVariant(root, opening) {
    if (!root || !opening) return;

    var variant = resolveVariant(root);
    opening.setAttribute('data-kid-opening-variant', variant);

    qa(opening, '[data-kid-opening-copy]').forEach(function (node) {
      node.hidden = node.getAttribute('data-kid-opening-copy') !== variant;
    });

    qa(opening, '[data-kid-opening-enter-label]').forEach(function (node) {
      node.hidden = node.getAttribute('data-kid-opening-enter-label') !== variant;
    });
  }

  function movePlatformIntoOpening(root, opening) {
    if (!root || !opening || root.__kidOpeningPlatformAnchor) return;

    const platform = q(root, '[data-kid-map-identity] [data-kid-map-platform-context]');

    const slot = q(opening, '[data-kid-opening-platform-slot]');

    if (!platform || !slot) return;

    const anchor = document.createComment('kid-platform-return');

    platform.parentNode.insertBefore(anchor, platform);

    root.__kidOpeningPlatformAnchor = anchor;
    root.__kidOpeningPlatformNode = platform;

    slot.appendChild(platform);
  }

  function restorePlatformFromOpening(root) {
    if (!(root instanceof HTMLElement)) return;

    const anchor = root.__kidOpeningPlatformAnchor;
    const platform = root.__kidOpeningPlatformNode;

    if (anchor && anchor.parentNode && platform) {
      anchor.parentNode.insertBefore(platform, anchor);
      anchor.remove();
    }

    root.__kidOpeningPlatformAnchor = null;
    root.__kidOpeningPlatformNode = null;
  }

  function lockBackground(root, opening) {
    if (!root || !opening) return;

    Array.prototype.slice.call(root.children).forEach(function (node) {
      if (node === opening || node.hasAttribute('data-kid-opening-inert-owned')) return;
      if (node.inert === true) return;

      node.inert = true;
      node.setAttribute('data-kid-opening-inert-owned', '');
    });

    document.documentElement.classList.add(LOCK_CLASS);
  }

  function unlockBackground(root) {
    if (root) {
      qa(root, '[data-kid-opening-inert-owned]').forEach(function (node) {
        node.inert = false;
        node.removeAttribute('data-kid-opening-inert-owned');
      });
    }

    document.documentElement.classList.remove(LOCK_CLASS);
  }

  function focusNode(node) {
    if (!node) return;

    try {
      node.focus({ preventScroll: true });
    } catch (_) {
      node.focus();
    }
  }

  function focusEnter(opening) {
    var button = q(opening, ENTER_SELECTOR);
    if (!button) return;

    window.requestAnimationFrame(function () {
      var panel = q(opening, '.kid-opening__panel');
      if (panel) panel.setAttribute('tabindex', '-1');
      focusNode(panel || button);
    });
  }

  function focusMap(root) {
    var canvas = q(root, '[data-gproperty-map-canvas]');
    if (!canvas) return;
    focusNode(canvas);
  }

  function finish(root, opening) {
    if (!root || !opening || !root.isConnected) return;

    restorePlatformFromOpening(root);

    root.setAttribute('data-kid-opening-state', 'ready');
    opening.hidden = true;
    opening.setAttribute('aria-hidden', 'true');

    unlockBackground(root);
    focusMap(root);

    try {
      document.dispatchEvent(
        new CustomEvent('littleg:kid-opening:ready', {
          bubbles: true,
          detail: {
            state: 'ready',
            variant: opening.getAttribute('data-kid-opening-variant') || '',
          },
        })
      );
    } catch (_) {}
  }

  function enter(root, opening, trigger) {
    if (!root || !opening || root.getAttribute('data-kid-opening-state') !== 'waiting') {
      return;
    }

    if (trigger) {
      trigger.disabled = true;
      trigger.setAttribute('aria-disabled', 'true');
    }

    root.setAttribute('data-kid-opening-state', 'entering');
    opening.setAttribute('data-kid-opening-phase', 'exit');

    try {
      document.dispatchEvent(
        new CustomEvent('littleg:kid-opening:enter', {
          bubbles: true,
          detail: {
            state: 'entering',
            variant: opening.getAttribute('data-kid-opening-variant') || '',
          },
        })
      );
    } catch (_) {}

    var duration = reducedMotion() ? 0 : EXIT_DURATION_MS;

    window.clearTimeout(root.__kidOpeningExitTimer);
    root.__kidOpeningExitTimer = window.setTimeout(function () {
      finish(root, opening);
    }, duration);
  }

  function bindVariantProjection(root, opening) {
    if (root.__kidOpeningVariantObserver || typeof MutationObserver !== 'function') return;

    var observer = new MutationObserver(function (mutations) {
      if (root.getAttribute('data-kid-opening-state') !== 'waiting') return;

      var relevant = mutations.some(function (mutation) {
        return (
          mutation.attributeName === 'data-kid-onboarding-state' ||
          mutation.attributeName === 'data-kid-invite-active'
        );
      });

      if (relevant) projectVariant(root, opening);
    });

    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-kid-onboarding-state', 'data-kid-invite-active'],
    });

    root.__kidOpeningVariantObserver = observer;
  }

  function bindSocialRefresh(root, opening) {
    if (root.__kidOpeningSocialHandler) return;

    root.__kidOpeningSocialHandler = function () {
      if (root.getAttribute('data-kid-opening-state') !== 'waiting') return;
      projectVariant(root, opening);
    };

    document.addEventListener('littleg:family-social-graph-change', root.__kidOpeningSocialHandler);
  }

  function mount(root) {
    if (!root || root.__kidOpeningVersion === VERSION) return;

    var opening = q(root, OPENING_SELECTOR);
    if (!opening) return;

    var trigger = q(opening, ENTER_SELECTOR);
    if (!trigger) return;

    /*
     * Initial waiting state is owned by
     * sections/littleg-kid-map-workspace.liquid.
     * This runtime owns only waiting -> entering -> ready.
     */
    if (root.getAttribute('data-kid-opening-state') !== 'waiting') return;

    // Dashboard is an internal map entry, not a first-use/Invite Opening.
    // Reuse finish() so aria-hidden, focus and the canonical ready event agree.
    if (isDashboardMapEntry()) {
      document.documentElement.setAttribute('data-littleg-map-entry', 'dashboard');
      root.__kidOpeningVersion = VERSION;
      finish(root, opening);
      return;
    }

    root.__kidOpeningVersion = VERSION;
    opening.hidden = false;
    opening.removeAttribute('aria-hidden');

    projectVariant(root, opening);
    movePlatformIntoOpening(root, opening);
    lockBackground(root, opening);
    bindVariantProjection(root, opening);
    bindSocialRefresh(root, opening);

    if (!trigger.__kidOpeningEnterBound) {
      trigger.__kidOpeningEnterBound = true;
      trigger.addEventListener('click', function () {
        enter(root, opening, trigger);
      });
    }

    window.clearTimeout(root.__kidOpeningSafetyTimer);
    root.__kidOpeningSafetyTimer = window.setTimeout(function () {
      if (root.getAttribute('data-kid-opening-state') === 'entering') {
        finish(root, opening);
      }
    }, 5000);

    focusEnter(opening);
  }

  function mountAll() {
    qa(document, ROOT_SELECTOR).forEach(mount);
  }

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target ? event.target.querySelector(ROOT_SELECTOR) : null;
    if (!(root instanceof HTMLElement)) return;

    window.clearTimeout(root.__kidOpeningExitTimer);
    window.clearTimeout(root.__kidOpeningSafetyTimer);

    if (root.__kidOpeningVariantObserver) {
      root.__kidOpeningVariantObserver.disconnect();
      root.__kidOpeningVariantObserver = null;
    }

    if (root.__kidOpeningSocialHandler) {
      document.removeEventListener(
        'littleg:family-social-graph-change',
        root.__kidOpeningSocialHandler
      );
      root.__kidOpeningSocialHandler = null;
    }

    restorePlatformFromOpening(root);
    unlockBackground(root);
    root.__kidOpeningVersion = null;
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll, { once: true });
  } else {
    mountAll();
  }

  document.addEventListener('shopify:section:load', mountAll);

  window.LittleGKidOpening = Object.freeze({
    version: VERSION,
    mount: mountAll,
    enter: function () {
      var root = document.querySelector(ROOT_SELECTOR);
      var opening = q(root, OPENING_SELECTOR);
      var trigger = q(opening, ENTER_SELECTOR);
      enter(root, opening, trigger);
    },
    state: function () {
      var root = document.querySelector(ROOT_SELECTOR);
      return root ? root.getAttribute('data-kid-opening-state') || '' : '';
    },
  });
})(window, document);
